import React, { useEffect, useRef } from 'react';
import Button from './Button';

/**
 * ConfirmModal (Dedicated confirmation dialog)
 *
 * Props:
 *   isOpen: boolean
 *   title: string | ReactNode
 *   message: string | ReactNode
 *   confirmText: string (default: 'Confirm')
 *   cancelText: string (default: 'Cancel')
 *   variant: 'danger' | 'warning' | 'primary' | 'info' | 'success' (default: 'danger')
 *   icon: string | ReactNode (optional emoji or icon)
 *   isLoading: boolean
 *   onConfirm: () => void
 *   onCancel: () => void
 */
const ConfirmModal = ({
  isOpen,
  title = 'Please Confirm',
  message = 'Are you sure you want to proceed with this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  icon,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const confirmBtnRef = useRef(null);

  // Focus confirm button when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        confirmBtnRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoading, onCancel]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Default icons based on variant
  const defaultIcon = {
    danger: '⚠️',
    warning: '⚡',
    primary: 'ℹ️',
    info: '💡',
    success: '✅',
  }[variant] || '⚠️';

  const iconDisplay = icon !== undefined ? icon : defaultIcon;

  // Button variant mapping
  const btnVariant = variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'primary';

  return (
    <div
      className="modal-backdrop confirm-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onCancel?.();
        }
      }}
    >
      <div
        className={`modal confirm-modal confirm-modal--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-modal__body">
          <div className={`confirm-modal__icon-wrap confirm-modal__icon-wrap--${variant}`}>
            <span className="confirm-modal__icon">{iconDisplay}</span>
          </div>
          <div className="confirm-modal__content">
            <h3 id="confirm-modal-title" className="confirm-modal__title">
              {title}
            </h3>
            <div id="confirm-modal-desc" className="confirm-modal__message">
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
          </div>
        </div>

        <div className="confirm-modal__footer">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            className="confirm-modal__cancel-btn"
          >
            {cancelText}
          </Button>
          <Button
            ref={confirmBtnRef}
            type="button"
            variant={btnVariant}
            onClick={onConfirm}
            disabled={isLoading}
            className="confirm-modal__confirm-btn"
          >
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
